/**
 * AES-256-GCM, used two ways:
 *
 *   - the master key (MK) encrypts each anchor payload
 *   - each KEK wraps the MK
 *
 * Everything is bound with additional authenticated data naming what it is and
 * which record it belongs to. That is what stops an attacker with write access
 * to IndexedDB from swapping anchor A's ciphertext into anchor B's row, or
 * replaying a wrapped MK from an old export into a fresh vault.
 */

import { concat, fromBase64, toBase64, utf8 } from './bytes.js';

export const IV_BYTES = 12;

export interface Sealed {
  /** base64 AES-GCM nonce */
  readonly iv: string;
  /** base64 ciphertext with the 16-byte tag appended */
  readonly ct: string;
}

export async function importAesKey(
  raw: Uint8Array,
  usages: KeyUsage[] = ['encrypt', 'decrypt'],
): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', raw as BufferSource, { name: 'AES-GCM' }, false, usages);
}

export async function generateMasterKey(): Promise<CryptoKey> {
  // Extractable: the MK has to be exported to be wrapped by each KEK.
  return crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, [
    'encrypt',
    'decrypt',
  ]);
}

export async function exportRawKey(key: CryptoKey): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.exportKey('raw', key));
}

function aad(context: string, recordId: string): Uint8Array {
  // Length-prefixed so ("ab","c") and ("a","bc") cannot collide.
  return concat(
    utf8(`v1|${context.length}|${context}|${recordId.length}|${recordId}`),
  );
}

export async function seal(
  key: CryptoKey,
  plaintext: Uint8Array,
  context: string,
  recordId: string,
): Promise<Sealed> {
  const iv = new Uint8Array(IV_BYTES);
  crypto.getRandomValues(iv);
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource, additionalData: aad(context, recordId) as BufferSource },
    key,
    plaintext as BufferSource,
  );
  return { iv: toBase64(iv), ct: toBase64(new Uint8Array(ct)) };
}

export class VaultDecryptionError extends Error {
  constructor(context: string) {
    super(`could not decrypt ${context}`);
    this.name = 'VaultDecryptionError';
  }
}

export async function open(
  key: CryptoKey,
  sealed: Sealed,
  context: string,
  recordId: string,
): Promise<Uint8Array> {
  try {
    const plain = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: fromBase64(sealed.iv) as BufferSource,
        additionalData: aad(context, recordId) as BufferSource,
      },
      key,
      fromBase64(sealed.ct) as BufferSource,
    );
    return new Uint8Array(plain);
  } catch {
    // Deliberately opaque: "wrong key" and "tampered ciphertext" are the same
    // answer to the caller, and distinguishing them helps only an attacker.
    throw new VaultDecryptionError(context);
  }
}

export async function sealJson(
  key: CryptoKey,
  value: unknown,
  context: string,
  recordId: string,
): Promise<Sealed> {
  return seal(key, utf8(JSON.stringify(value)), context, recordId);
}

export async function openJson<T>(
  key: CryptoKey,
  sealed: Sealed,
  context: string,
  recordId: string,
): Promise<T> {
  const bytes = await open(key, sealed, context, recordId);
  return JSON.parse(new TextDecoder().decode(bytes)) as T;
}
